import type { Edge, HealthValue } from '../api/types.js';

// Edge node metric names (EDGE_NODE_METRICS in @kalvio/contracts). Duplicated
// here so the browser bundle does not pull in the zod contract schemas.
export const HEALTH_METRICS = {
  uptimeS: 'Health/Uptime s',
  bufferDepth: 'Health/Buffer Depth',
  bufferOldestAgeS: 'Health/Buffer Oldest Age s',
  bufferDropped: 'Health/Buffer Dropped',
  cpuPercent: 'Health/CPU %',
  memoryMb: 'Health/Memory MB',
  diskFreeMb: 'Health/Disk Free MB',
  bacnetState: 'Drivers/bacnet/State',
  bacnetDevicesOnline: 'Drivers/bacnet/Devices Online',
  bacnetDevices: 'Drivers/bacnet/Devices',
  bacnetTimeouts: 'Drivers/bacnet/Timeouts',
  bacnetErrors: 'Drivers/bacnet/Errors',
  agentVersion: 'Properties/Agent Version',
  /** WP6: rules bundle version the edge runs. */
  rulesVersion: 'Rules/Version',
  /** WP6: JSON per rule id (state, last evaluation, last fire, error). */
  rulesStatus: 'Rules/Status',
} as const;

export interface EdgeHealth {
  uptimeS: number | null;
  bufferDepth: number | null;
  bufferOldestAgeS: number | null;
  bufferDropped: number | null;
  cpuPercent: number | null;
  memoryMb: number | null;
  diskFreeMb: number | null;
  bacnetState: string | null;
  bacnetDevicesOnline: number | null;
  bacnetDevices: number | null;
  bacnetTimeouts: number | null;
  bacnetErrors: number | null;
  agentVersion: string | null;
  rulesVersion: number | null;
}

function num(v: HealthValue | undefined): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function str(v: HealthValue | undefined): string | null {
  if (v === null || v === undefined) return null;
  return String(v);
}

/** Typed view of an edge's health metrics; missing or malformed values are null. */
export function edgeHealth(edge: Pick<Edge, 'health' | 'agent_version'>): EdgeHealth {
  const h = edge.health ?? {};
  const m = HEALTH_METRICS;
  return {
    uptimeS: num(h[m.uptimeS]),
    bufferDepth: num(h[m.bufferDepth]),
    bufferOldestAgeS: num(h[m.bufferOldestAgeS]),
    bufferDropped: num(h[m.bufferDropped]),
    cpuPercent: num(h[m.cpuPercent]),
    memoryMb: num(h[m.memoryMb]),
    diskFreeMb: num(h[m.diskFreeMb]),
    bacnetState: str(h[m.bacnetState]),
    bacnetDevicesOnline: num(h[m.bacnetDevicesOnline]),
    bacnetDevices: num(h[m.bacnetDevices]),
    bacnetTimeouts: num(h[m.bacnetTimeouts]),
    bacnetErrors: num(h[m.bacnetErrors]),
    agentVersion: edge.agent_version ?? str(h[m.agentVersion]),
    rulesVersion: num(h[m.rulesVersion]),
  };
}
